import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Phone, Check } from "lucide-react";

type Cb = { id: string; customerName?: string; customerPhone?: string; status: string; requestedAt?: string; priority?: string };

export default function CallbackQueue() {
  const list = useQuery<Cb[]>({ queryKey: ["/api/callback-requests"] });
  const start = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/callback-requests/${id}/start-call`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/callback-requests"] }); queryClient.invalidateQueries({ queryKey: ["/api/calls"] }); },
  });
  const complete = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/callback-requests/${id}/complete`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/callback-requests"] }),
  });
  const data = list.data || [];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4" data-testid="page-callbacks">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Callback Queue</h1>
        <p className="text-sm text-muted-foreground">{data.length} pending callback(s)</p>
      </div>
      <div className="space-y-2">
        {data.map(c => (
          <Card key={c.id} data-testid={`card-cb-${c.id}`}>
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{c.customerName || c.customerPhone}</div>
                <div className="text-xs text-muted-foreground">{c.requestedAt && new Date(c.requestedAt).toLocaleString()} · {c.priority || "normal"}</div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{c.status}</Badge>
                <Button size="sm" onClick={() => start.mutate(c.id)} data-testid={`button-start-call-${c.id}`}>
                  <Phone className="w-3 h-3 mr-1" />Start call
                </Button>
                <Button size="sm" variant="outline" onClick={() => complete.mutate(c.id)} data-testid={`button-complete-cb-${c.id}`}>
                  <Check className="w-3 h-3 mr-1" />Complete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {data.length === 0 && <p className="text-center text-sm text-muted-foreground py-12">No callbacks.</p>}
      </div>
    </div>
  );
}
